import { z } from 'zod';
import { HttpErrorResponse } from '../common/validation-error';
const PositiveId = z.coerce.number().int().positive();
const Slug = z.string().min(1);
const MessageResponse = z.string();
const SubmissionResponse = z.object({
    success: z.boolean(),
    message: z.string(),
});
const AnswerState = z
    .object({
    value: z.string(),
    is_correct: z.boolean().nullable(),
    is_submitted: z.boolean(),
    submitted_at: z.string().nullable(),
    failed_correctness_checks: z.array(z.string()).optional(),
    working_code: z.string().optional(),
    result: z.string().nullable().optional(),
    review: z.string().nullable().optional(),
    does_satisfy_all_requirements: z.boolean().nullable().optional(),
})
    .passthrough();
const ActivitySubmission = z.object({
    difficulty_rating: z.number().nullable(),
    feedback: z.string().nullable(),
    activity_session_id: PositiveId,
    seconds_spent: z.number().nonnegative(),
    activity_id: PositiveId,
    answer_states: z.record(AnswerState),
});
const ProjectSubmission = z.object({
    difficulty_rating: z.number().nullable(),
    feedback: z.string().nullable(),
    activity_session_id: PositiveId,
    seconds_spent: z.number().nonnegative(),
    activity_id: PositiveId,
    answer_states: z.record(AnswerState),
});
export const submitTeamsActivityRoute = {
    version: 1,
    method: 'POST',
    path: '/tracks/{trackSlug}/activities/submit',
    name: 'submit-learner-activity',
    request: {
        params: z.object({ trackSlug: Slug }),
        body: ActivitySubmission,
    },
    responses: { 200: MessageResponse, 400: HttpErrorResponse },
};
export const submitTeamsProjectRoute = {
    version: 1,
    method: 'POST',
    path: '/teams/{companySlug}/projects/{projectSlug}/submit',
    name: 'submit-teams-project',
    request: {
        params: z.object({ companySlug: Slug, projectSlug: Slug }),
        body: ProjectSubmission,
    },
    responses: {
        200: MessageResponse,
        400: HttpErrorResponse,
        404: HttpErrorResponse,
    },
};
/** 50 MiB. Enforced by the browser, the API and the signed S3 upload URL. */
export const COMPANY_PROJECT_MAX_FILE_BYTES = 52428800;
export const COMPANY_PROJECT_MAX_FILES = 10;
export const COMPANY_PROJECT_UPLOAD_LINK_EXPIRY_SECONDS = 900;
const CompanyProjectSubmissionType = z.enum([
    'CHECKIN_1',
    'CHECKIN_2',
    'PROJECT_UPLOAD',
]);
/** Files are already in S3; `uploadedKeys` are the keys the upload links returned. */
export const SubmitCohortProjectJsonBody = z.object({
    submissionType: CompanyProjectSubmissionType,
    survey: z.array(z.object({ question: z.string(), answer: z.string() })),
    links: z.array(z.string()),
    retainedFileLinks: z.array(z.string()),
    uploadedKeys: z.array(z.string()).max(COMPANY_PROJECT_MAX_FILES),
});
export const submitCohortProjectRoute = {
    version: 1,
    method: 'POST',
    path: '/teams/cohorts/{cohortId}/learners/{learnerId}/project',
    name: 'submit-cohort-project',
    request: {
        params: z.object({ cohortId: PositiveId, learnerId: PositiveId }),
        // JSON from the upload page, or a multipart string from the check-in pages.
        body: z.union([SubmitCohortProjectJsonBody, z.string()]),
    },
    responses: {
        200: MessageResponse,
        400: HttpErrorResponse,
        403: HttpErrorResponse,
    },
};
/** A signed PUT URL; send the file as the body with this Content-Type. */
const CompanyProjectUploadLink = z.object({
    key: z.string(),
    fileName: z.string(),
    url: z.string(),
    contentType: z.string(),
});
export const createCompanyProjectUploadLinksRoute = {
    version: 1,
    method: 'POST',
    path: '/teams/cohorts/{cohortId}/learners/{learnerId}/project/upload-links',
    name: 'create-company-project-upload-links',
    request: {
        params: z.object({ cohortId: PositiveId, learnerId: PositiveId }),
        body: z.object({
            files: z
                .array(z.object({
                name: z.string().min(1).max(255),
                size: z
                    .number()
                    .int()
                    .positive()
                    .max(COMPANY_PROJECT_MAX_FILE_BYTES),
                contentType: z.string().max(255),
            }))
                .min(1)
                .max(COMPANY_PROJECT_MAX_FILES),
        }),
    },
    responses: {
        200: z.object({
            uploads: z.array(CompanyProjectUploadLink),
            expiresInSeconds: z.number(),
        }),
        400: HttpErrorResponse,
        403: HttpErrorResponse,
    },
};
export const submitTeamsProjectAnswerRoute = {
    version: 1,
    method: 'POST',
    path: '/teams/{companySlug}/projects/{projectSlug}/submit-answer/',
    name: 'submit-teams-project-exercise-answer',
    request: {
        params: z.object({ companySlug: Slug, projectSlug: Slug }),
        body: z.object({
            value: z.string().min(1),
            submissionId: PositiveId,
            questionSlug: Slug,
        }),
    },
    responses: { 200: SubmissionResponse, 400: HttpErrorResponse },
};
export const submitTeamsAnswerRoute = {
    version: 1,
    method: 'POST',
    path: '/learner/submit-answer/',
    name: 'submit-teams-answer-attempt',
    request: {
        body: z.object({
            value: z.string().min(1),
            activitySessionId: PositiveId,
            questionSlug: Slug,
        }),
    },
    responses: { 200: SubmissionResponse, 400: HttpErrorResponse },
};
